import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { format } from 'date-fns';
import { Download } from 'lucide-react';
import toast from 'react-hot-toast';
import { belegeAPI, mandantenAPI } from '../lib/api';
import UploadZone from '../components/UploadZone';
import DatevExportModal from '../components/DatevExportModal';

const STATUS_LABELS = {
  eingang: 'Eingang',
  extracting: 'Extraktion läuft',
  extrahiert: 'Extrahiert',
  vorgeschlagen: 'Vorgeschlagen',
  review: 'Review',
  gebucht: 'Gebucht',
  exportiert: 'Exportiert',
  verworfen: 'Verworfen',
};

const STATUS_FILTERS = ['', 'review', 'extrahiert', 'vorgeschlagen', 'gebucht', 'exportiert'];

function fmtMoney(v) {
  if (v === null || v === undefined) return '—';
  return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(v);
}

function fmtDate(s) {
  if (!s) return '—';
  return format(new Date(s), 'dd.MM.yyyy');
}

export default function BelegeList() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [mandant, setMandant] = useState(null);
  const [belege, setBelege] = useState([]);
  const [statusFilter, setStatusFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [uploads, setUploads] = useState([]);
  const [datevOpen, setDatevOpen] = useState(false);

  useEffect(() => {
    mandantenAPI.get(slug).then((r) => setMandant(r.data)).catch(() => {});
  }, [slug]);

  const reloadBelege = useCallback(() => {
    setLoading(true);
    return belegeAPI
      .list(slug, { status: statusFilter || undefined })
      .then((r) => setBelege(r.data))
      .catch((err) =>
        toast.error(err.response?.data?.detail || 'Belege konnten nicht geladen werden')
      )
      .finally(() => setLoading(false));
  }, [slug, statusFilter]);

  useEffect(() => {
    reloadBelege();
  }, [reloadBelege]);

  // Auto-poll while any beleg is in a transient state (eingang or extracting).
  // Silent reload so the table doesn't flash a loading spinner.
  useEffect(() => {
    const transient = belege.some(
      (b) => b.status === 'eingang' || b.status === 'extracting'
    );
    if (!transient) return undefined;
    const handle = setInterval(() => {
      belegeAPI
        .list(slug, { status: statusFilter || undefined })
        .then((r) => setBelege(r.data))
        .catch(() => {});
    }, 3000);
    return () => clearInterval(handle);
    // depend on a content-stable string: only re-arm when the set of statuses changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [belege.map((b) => b.status).join(','), slug, statusFilter]);

  const handleUpload = async (files) => {
    // Add all as pending
    const newItems = files.map((f) => ({ name: f.name, file: f, status: 'pending' }));
    setUploads((prev) => [...prev, ...newItems]);

    // Sequential upload — keeps backend BG-task queue manageable
    for (let i = 0; i < newItems.length; i++) {
      const item = newItems[i];
      setUploads((prev) =>
        prev.map((u) => (u === item ? { ...u, status: 'uploading' } : u))
      );
      try {
        const r = await belegeAPI.upload(slug, item.file);
        setUploads((prev) =>
          prev.map((u) =>
            u === item
              ? { ...u, status: 'done', message: `→ Beleg #${r.data.id}` }
              : u
          )
        );
      } catch (err) {
        const msg = err.response?.data?.detail || 'Upload fehlgeschlagen';
        setUploads((prev) =>
          prev.map((u) => (u === item ? { ...u, status: 'error', message: msg } : u))
        );
      }
    }

    // Refresh table after all uploads complete
    await reloadBelege();
  };

  return (
    <div className="page">
      <header className="page-header">
        <div>
          <h1>Belege</h1>
          {mandant && (
            <div className="muted">
              {mandant.name} · {mandant.skr_variant}
            </div>
          )}
        </div>
      </header>

      <UploadZone uploads={uploads} onUpload={handleUpload} onClear={() => setUploads([])} />

      <div className="filters">
        <label>
          Status:
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            {STATUS_FILTERS.map((s) => (
              <option key={s} value={s}>
                {s ? STATUS_LABELS[s] : 'Alle'}
              </option>
            ))}
          </select>
        </label>
        <button className="btn-text" onClick={reloadBelege} disabled={loading}>
          {loading ? 'Lädt…' : 'Neu laden'}
        </button>
        <button
          className="btn-secondary"
          style={{ marginLeft: 'auto' }}
          onClick={() => setDatevOpen(true)}
        >
          <Download size={14} /> DATEV-Export
        </button>
      </div>

      <DatevExportModal
        open={datevOpen}
        onClose={() => setDatevOpen(false)}
        mandant={mandant}
        onExported={reloadBelege}
      />

      {loading ? (
        <div className="muted">Lädt…</div>
      ) : belege.length === 0 ? (
        <div className="card empty">Keine Belege gefunden.</div>
      ) : (
        <table className="data-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Status</th>
              <th>Datum</th>
              <th>Rechnungs-Nr</th>
              <th>Brutto</th>
              <th>Hochgeladen</th>
            </tr>
          </thead>
          <tbody>
            {belege.map((b) => (
              <tr
                key={b.id}
                className="row-link"
                onClick={() => navigate(`/mandanten/${slug}/belege/${b.id}`)}
              >
                <td>#{b.id}</td>
                <td>
                  <span className={`badge badge-${b.status}`}>
                    {STATUS_LABELS[b.status] || b.status}
                  </span>
                </td>
                <td>{fmtDate(b.beleg_datum)}</td>
                <td>{b.rechnungsnummer || '—'}</td>
                <td className="num">{fmtMoney(b.betrag_brutto)}</td>
                <td className="muted">{fmtDate(b.uploaded_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
